/**
 * The bearer token for API calls. One issuer: 42QLabs central identity.
 *
 * Supabase authentication was removed, not disabled — so there is no second source to
 * fall back to and no precedence rule to get wrong. A token that has expired is
 * refreshed through the backend, which holds the client secret; the browser never sees
 * it.
 *
 * Refresh uses `fetch` rather than the axios client on purpose: the client's request
 * interceptor calls into this module, so importing it here would be a cycle — and the
 * one request that must work when the token is stale is this one.
 */

const TOKEN_KEY = 'plutus.oidc.access_token'
const REFRESH_KEY = 'plutus.oidc.refresh_token'
const EXPIRY_KEY = 'plutus.oidc.expires_at'

export function storeSession(token: string, expiresInSeconds: number,
                             refreshToken?: string | null): void {
  try {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(EXPIRY_KEY,
      String(Date.now() + Math.max(60, expiresInSeconds) * 1000))
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken)
  } catch { /* private window — the session will not persist */ }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(EXPIRY_KEY)
  } catch { /* ignore */ }
}

export function hasSession(): boolean {
  try { return !!localStorage.getItem(TOKEN_KEY) } catch { return false }
}

function raw(): string | null {
  try { return localStorage.getItem(TOKEN_KEY) } catch { return null }
}

function expiresAt(): number {
  try { return Number(localStorage.getItem(EXPIRY_KEY) || 0) } catch { return 0 }
}

// A single in-flight refresh, shared by every caller. Without this, a page that fires
// six requests on load would start six refreshes and five would fail on a rotated token.
let inflight: Promise<string | null> | null = null

async function refresh(): Promise<string | null> {
  let rt: string | null = null
  try { rt = localStorage.getItem(REFRESH_KEY) } catch { rt = null }
  if (!rt) { clearSession(); return null }
  try {
    const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    storeSession(data.access_token, data.expires_in, data.refresh_token)
    return data.access_token as string
  } catch {
    clearSession()
    return null
  }
}

/** The token for the next request, refreshed if it is close to expiry. */
export async function accessToken(): Promise<string | null> {
  const t = raw()
  if (!t) return null
  // Refreshed 60s early: a token that expires mid-flight reads as a broken app.
  if (Date.now() < expiresAt() - 60_000) return t
  if (!inflight) inflight = refresh().finally(() => { inflight = null })
  return inflight
}
